import * as React from "react";
import * as Load from "../load";
import styled from "styled-components";
import { Props } from "../types";
import { TabContainer } from "./tab-container";
import { Action } from "../state";
import { Dispatch } from "@typescript-tea/core";
import { PropertyDefinitions } from "../../../../tools";
import { PropertyValueSet } from "@genesys/property";
import { BorderRadius, PrimaryColors } from "@genesys/ui-elements";
import { CustomTab } from "./custom-tab";
import { GetErrorSeverity } from "../tools";

const StyledTab = styled.li`
  cursor: pointer;
`;

const ActiveTab = styled(StyledTab)`
  border-bottom: 5px solid ${PrimaryColors.muntersBlue};
`;

const SingeLoadViewContainer = styled.div<{
  readonly errorSeverity: Load.ErrorSeverity;
}>`
  border-radius: ${BorderRadius.standard};
  border: 2.5px solid
    ${props =>
      props.errorSeverity === "error"
        ? "#E8767B"
        : props.errorSeverity === "warning"
        ? "#F0C530"
        : "#d3d3d3"};
  padding: 5px;
`;

export const SingleLoadView = (props: Props) => {
  const { state, isDisabled, loadDef, sharedState, scrollRef, dispatch } =
    props;

  React.useEffect(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const activeLoadStates = Object.values(state.activeLoads);
  const selectedLoadState = state.selectedLoadId
    ? state.activeLoads[state.selectedLoadId]
    : activeLoadStates[0];

  const isSelectedLoadDuplicated =
    activeLoadStates.filter(load => load.name === selectedLoadState.name)
      .length > 1;

  const errorSeverity = GetErrorSeverity(
    state,
    loadDef,
    props.currentTopLevelMoistureLoadSettings
  );

  return (
    <SingeLoadViewContainer errorSeverity={errorSeverity}>
      <Tabs
        activeLoads={activeLoadStates}
        loadDef={loadDef}
        currentTopLevelMoistureLoadSettings={
          props.currentTopLevelMoistureLoadSettings
        }
        propertyDefinitions={state.propertyDefinitions}
        dispatch={dispatch}
        selectedLoad={selectedLoadState.id}
        isReadOnly={isDisabled}
      />
      <Load.View
        isLoadNameDuplicated={isSelectedLoadDuplicated}
        headerType="single"
        state={selectedLoadState}
        sharedState={sharedState}
        currentTopLevelCaseOverrides={props.currentTopLevelCaseOverrides}
        propertyDefinitions={state.propertyDefinitions}
        loadDef={loadDef}
        isDisabled={isDisabled}
        currentTopLevelMoistureLoadSettings={
          props.currentTopLevelMoistureLoadSettings
        }
        dispatch={Dispatch.map(
          action => Action.dispatchLoad(selectedLoadState.id, action, loadDef),
          dispatch
        )}
      />
    </SingeLoadViewContainer>
  );
};

const Tabs = ({
  activeLoads,
  selectedLoad,
  loadDef,
  isReadOnly,
  propertyDefinitions,
  currentTopLevelMoistureLoadSettings,
  dispatch
}: {
  readonly selectedLoad: string;
  readonly activeLoads: ReadonlyArray<Load.ActiveLoadState>;
  readonly loadDef: Load.LoadDef;
  readonly isReadOnly: boolean;
  readonly propertyDefinitions: PropertyDefinitions;
  readonly currentTopLevelMoistureLoadSettings: PropertyValueSet.PropertyValueSet;
  readonly dispatch: Dispatch<Action>;
}) => {
  const errorSeverity = (name: string, id: string) => {
    const isLoadNameDuplicated = activeLoads.some(
      load => load.name === name && load.id !== id
    );

    const load = activeLoads.find(load => load.id === id);
    if (load !== undefined) {
      return isLoadNameDuplicated
        ? "error"
        : Load.GetErrorSeverity(
            load,
            loadDef,
            currentTopLevelMoistureLoadSettings,
            propertyDefinitions
          );
    }
    return "none";
  };

  return (
    <TabContainer
      selectedTabIndex={activeLoads.findIndex(load => load.id === selectedLoad)}
    >
      {activeLoads.map(load => (
        <Tab
          errorSeverity={errorSeverity(load.name, load.id)}
          key={load.id}
          name={load.name}
          isSelected={load.id === selectedLoad}
          isReadOnly={isReadOnly}
          data-tab-id={load.id}
          onChange={value => {
            dispatch(Action.updateLoadName(load.id, value));
          }}
          onClick={() => {
            dispatch(Action.setSelectedLoad(load.id));
          }}
        />
      ))}
    </TabContainer>
  );
};

const Tab = ({
  name,
  isSelected,
  isReadOnly,
  errorSeverity,
  onClick,
  onChange
}: {
  readonly name: string;
  readonly isSelected: boolean;
  readonly isReadOnly: boolean;
  readonly errorSeverity: Load.ErrorSeverity;
  readonly onClick: () => void;
  readonly onChange: (value: string) => void;
}) => {
  const Container = isSelected ? ActiveTab : StyledTab;

  return (
    <Container onClick={() => !isSelected && onClick()}>
      <CustomTab
        value={name}
        onChange={onChange}
        isReadOnly={isReadOnly}
        hasError={errorSeverity}
      />
    </Container>
  );
};
